/**
 * TrackControls Component
 * Individual track controls for the DAW workspace
 * Extracted from DAWWorkspace.jsx for better modularity
 */

import { useRef } from 'react';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { 
  Volume2, 
  Trash2, 
  Circle, 
  Upload,
} from 'lucide-react';

export const TrackControls = ({ 
  track, 
  isSelected, 
  isRecording, 
  onSelect, 
  onUpdate, 
  onRemove, 
  onRecord, 
  onImport, 
  canRemove, 
  onMidiRecord, 
  isMidiRecording, 
  collabUsers, 
  currentUserId, 
  showCollaboration 
}) => {
  const fileInputRef = useRef(null);
  const isInstrumentTrack = track.type === 'instrument';
  
  // Check if other users are editing this track
  const editingUsers = (collabUsers || []).filter(
    user => user.selectedTrack === track.id && user.id !== currentUserId
  );
  const isBeingEditedByOthers = editingUsers.length > 0;
  
  return (
    <div
      className={`p-3 border-b border-zinc-800 cursor-pointer transition-colors relative ${
        isSelected ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'
      }`}
      onClick={onSelect}
      style={isBeingEditedByOthers ? {
        borderLeftWidth: '3px',
        borderLeftColor: editingUsers[0]?.color || '#6366f1',
      } : {}}
      data-testid={`track-controls-${track.id}`}
    >
      {/* Collaboration Indicator */}
      {showCollaboration && isBeingEditedByOthers && (
        <div className="absolute right-2 top-2 flex gap-1" data-testid={`track-collab-indicator-${track.id}`}>
          {editingUsers.slice(0, 2).map(user => (
            <div
              key={user.id}
              className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white ring-2 ring-zinc-900"
              style={{ backgroundColor: user.color }}
              title={`${user.name} is editing`}
            >
              {user.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          ))}
          {editingUsers.length > 2 && (
            <div className="w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] text-zinc-300">
              +{editingUsers.length - 2}
            </div>
          )}
        </div>
      )}
      
      {/* Track Header */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${track.color}`} />
        {isInstrumentTrack && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
            {track.instrumentType === 'drums' ? 'DRUMS' : 'SYNTH'}
          </span>
        )}
        <input
          type="text"
          value={track.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 bg-transparent text-sm font-medium outline-none"
        />
        {canRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            className="text-zinc-500 hover:text-red-400 p-1 h-auto"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
      
      {/* Record / Import - Different for instrument vs audio tracks */}
      <div className="flex gap-1 mb-2">
        {isInstrumentTrack ? (
          // MIDI Recording Button
          <Button
            variant={isMidiRecording && isSelected ? 'default' : 'outline'}
            size="sm"
            onClick={(e) => { e.stopPropagation(); onMidiRecord?.(); }}
            className={`flex-1 h-7 text-xs ${isMidiRecording && isSelected ? 'bg-red-500 hover:bg-red-600' : 'border-purple-500/30 text-purple-300'}`}
          >
            <Circle className={`w-3 h-3 mr-1 ${isMidiRecording && isSelected ? 'animate-pulse' : ''}`} fill={isMidiRecording && isSelected ? 'currentColor' : 'none'} />
            {isMidiRecording && isSelected ? 'Stop MIDI' : 'Rec MIDI'}
          </Button>
        ) : (
          // Audio Recording / Import Buttons
          <>
            <Button
              variant={isRecording ? 'default' : 'outline'}
              size="sm"
              onClick={(e) => { e.stopPropagation(); onRecord(); }}
              className={`flex-1 h-7 text-xs ${isRecording ? 'bg-red-500 hover:bg-red-600' : 'border-zinc-700'}`}
            >
              <Circle className={`w-3 h-3 mr-1 ${isRecording ? 'animate-pulse' : ''}`} fill={isRecording ? 'currentColor' : 'none'} />
              {isRecording ? 'Stop' : 'Rec'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
              className="flex-1 h-7 text-xs border-zinc-700"
            >
              <Upload className="w-3 h-3 mr-1" />
              Import
            </Button>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          className="hidden"
          data-testid={`track-${track.id}-file-input`}
          onChange={(e) => {
            if (e.target.files?.[0]) {
              onImport(e.target.files[0]);
            }
          }}
        />
      </div>
      
      {/* Volume */}
      <div className="flex items-center gap-2 mb-2">
        <Volume2 className="w-3 h-3 text-zinc-500" />
        <Slider
          value={[track.volume * 100]}
          onValueChange={([v]) => onUpdate({ volume: v / 100 })}
          max={100}
          step={1}
          className="flex-1"
          onClick={(e) => e.stopPropagation()}
        />
        <span className="text-xs text-zinc-500 w-8">{Math.round(track.volume * 100)}</span>
      </div>
      
      {/* Pan */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs text-zinc-500">Pan</span>
        <Slider
          value={[track.pan * 50 + 50]}
          onValueChange={([v]) => onUpdate({ pan: (v - 50) / 50 })}
          max={100}
          step={1}
          className="flex-1"
          onClick={(e) => e.stopPropagation()}
        />
        <span className="text-xs text-zinc-500 w-8">
          {track.pan === 0 ? 'C' : track.pan < 0 ? `L${Math.abs(Math.round(track.pan * 100))}` : `R${Math.round(track.pan * 100)}`}
        </span>
      </div>
      
      {/* Mute / Solo */}
      <div className="flex gap-1">
        <Button
          variant={track.muted ? 'default' : 'outline'}
          size="sm"
          onClick={(e) => { e.stopPropagation(); onUpdate({ muted: !track.muted }); }}
          className={`flex-1 h-6 text-xs ${track.muted ? 'bg-red-500/80 hover:bg-red-500' : 'border-zinc-700'}`}
        >
          M
        </Button>
        <Button
          variant={track.solo ? 'default' : 'outline'}
          size="sm"
          onClick={(e) => { e.stopPropagation(); onUpdate({ solo: !track.solo }); }}
          className={`flex-1 h-6 text-xs ${track.solo ? 'bg-amber-500/80 hover:bg-amber-500' : 'border-zinc-700'}`}
        >
          S
        </Button>
      </div>
    </div>
  );
};

export default TrackControls;
